/**
 * sources.ts - Consulta Paralela Multi-Fuente
 * 
 * Módulo extraído de crossValidation.ts (TD-002.G.3)
 * Implementa la consulta paralela de múltiples fuentes de geocodificación
 * con tolerancia a fallos usando Promise.allSettled.
 * 
 * @created 2026-01-26
 */

import {
  DEFAULT_SOURCE_TIMEOUT_MS,
  DEFAULT_CROSS_VALIDATION_LEVEL,
  LegacyTimeoutError,
  LegacyNoResultsError,
  type UTMPoint,
  type GeocodingSourceId as _GeocodingSourceId,
  type GeocodingSource,
  type GeocodingQuery,
  type SourceResult,
} from './types';

import {
  createErrorContext,
  createError,
  getRecommendedAction,
  wrapError,
  type GeocodingError,
} from '../../types/errors';

// ============================================================================
// CONSULTA CON TIMEOUT (privado)
// ============================================================================

/**
 * Consulta una fuente individual con timeout.
 * Integrado con sistema de errores tipados (F023 Fase 2).
 * 
 * @internal
 */
async function querySourceWithTimeout(
  source: GeocodingSource,
  query: GeocodingQuery
): Promise<SourceResult> {
  const timeoutMs = source.timeoutMs ?? DEFAULT_SOURCE_TIMEOUT_MS;
  const startTime = performance.now();
  
  // Crear contexto para errores tipados (source.id ya es canónico lowercase)
  const errorContext = createErrorContext(source.id, DEFAULT_CROSS_VALIDATION_LEVEL, {
    elementName: query.nombre,
    municipio: query.municipio,
    codigoINE: query.codMun,
  });
  
  // Crear promesa de timeout
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new LegacyTimeoutError(
        `Timeout consultando ${source.name}`,
        timeoutMs,
        { source: source.id, query: query.nombre }
      ));
    }, timeoutMs);
  });

  try {
    // Carrera entre geocodificación y timeout
    const result = await Promise.race([
      source.geocode(query),
      timeoutPromise
    ]);
    
    const responseTimeMs = performance.now() - startTime;
    
    if (result === null) {
      // Crear error tipado de no resultados
      const typedError = createError.noResults(
        errorContext,
        query.nombre,
        { municipio: query.municipio ?? '', tipologia: query.tipologia ?? '' }
      );
      const action = getRecommendedAction(typedError);
      
      return {
        sourceId: source.id,
        sourceName: source.name,
        authorityWeight: source.authorityWeight,
        result: null,
        typedError,
        error: new LegacyNoResultsError(
          `Sin resultados en ${source.name}`,
          [source.id],
          { source: source.id, query: query.nombre }
        ),
        recommendedAction: action,
        responseTimeMs,
        status: 'no_results'
      };
    }
    
    return {
      sourceId: source.id,
      sourceName: source.name,
      authorityWeight: source.authorityWeight,
      result,
      typedError: null,
      error: null,
      recommendedAction: null,
      responseTimeMs,
      status: 'success'
    };
    
  } catch (error) {
    const responseTimeMs = performance.now() - startTime;
    
    // IMPORTANTE: Determinar status ANTES de envolver el error
    // porque wrapError() transforma el error y pierde la instancia original
    let status: SourceResult['status'] = 'error';
    let typedError: GeocodingError;
    
    if (error instanceof LegacyTimeoutError) {
      status = 'timeout';
      typedError = createError.timeout(
        errorContext,
        source.name,
        timeoutMs
      );
    } else if (error instanceof LegacyNoResultsError) {
      status = 'no_results';
      typedError = createError.noResults(
        errorContext,
        query.nombre
      );
    } else {
      // Error genérico de conexión
      typedError = createError.connection(
        errorContext,
        source.name,
        'unknown'
      );
    }
    
    // Envolver error para compatibilidad legacy (después de determinar status)
    const wrappedError = wrapError(error, {
      source: source.id,
      query: query.nombre
    });
    
    const action = getRecommendedAction(typedError);
    
    return {
      sourceId: source.id,
      sourceName: source.name,
      authorityWeight: source.authorityWeight,
      result: null,
      typedError,
      error: wrappedError,
      recommendedAction: action,
      responseTimeMs,
      status
    };
  }
}

// ============================================================================
// CONSULTA PARALELA MULTI-FUENTE (público)
// ============================================================================

/**
 * Consulta múltiples fuentes de geocodificación en paralelo.
 * 
 * Usa Promise.allSettled para tolerancia a fallos: si una fuente falla,
 * las demás continúan. Cada fuente tiene su propio timeout.
 * 
 * @param query - Query de geocodificación
 * @param sources - Array de fuentes a consultar
 * @param globalTimeoutMs - Timeout global opcional (default: sin límite global)
 * @returns Array de resultados, uno por cada fuente
 * 
 * @example
 * const results = await queryMultipleSources(
 *   { nombre: 'Centro de Salud', codMun: '04013', tipologia: 'SANITARIO' },
 *   [localSource, cartoCiudadSource, cdauSource]
 * );
 */
export async function queryMultipleSources(
  query: GeocodingQuery,
  sources: GeocodingSource[],
  globalTimeoutMs?: number
): Promise<SourceResult[]> {
  if (sources.length === 0) {
    return [];
  }

  // Crear promesas con timeout individual para cada fuente
  const sourcePromises = sources.map(source => 
    querySourceWithTimeout(source, query)
  );

  // Si hay timeout global, envolver todo en una carrera
  let results: PromiseSettledResult<SourceResult>[];
  
  if (globalTimeoutMs) {
    const globalTimeout = new Promise<never>((_, reject) => 
      setTimeout(() => reject(new LegacyTimeoutError(
        'Timeout global de validación cruzada',
        globalTimeoutMs
      )), globalTimeoutMs)
    );
    
    try {
      results = await Promise.race([
        Promise.allSettled(sourcePromises),
        globalTimeout.then(() => { throw new Error('timeout'); })
      ]) as PromiseSettledResult<SourceResult>[];
    } catch {
      // Si el timeout global dispara, devolvemos lo que tengamos
      results = await Promise.allSettled(sourcePromises);
    }
  } else {
    results = await Promise.allSettled(sourcePromises);
  }

  // Transformar resultados de allSettled a SourceResult[]
  return results.map((settled, index) => {
    if (settled.status === 'fulfilled') {
      return settled.value;
    } else {
      // Promesa rechazada - crear SourceResult de error
      const source = sources[index];
      const error = wrapError(settled.reason, {
        source: source.id,
        query: query.nombre
      });
      
      // Crear error tipado para promesas rechazadas (source.id ya es canónico)
      const errorContext = createErrorContext(source.id, DEFAULT_CROSS_VALIDATION_LEVEL, {
        elementName: query.nombre,
        municipio: query.municipio,
        codigoINE: query.codMun,
      });
      const typedError = createError.connection(errorContext, source.name, 'unknown');
      const action = getRecommendedAction(typedError);
      
      return {
        sourceId: source.id,
        sourceName: source.name,
        authorityWeight: source.authorityWeight,
        result: null,
        typedError,
        error,
        recommendedAction: action,
        responseTimeMs: source.timeoutMs ?? DEFAULT_SOURCE_TIMEOUT_MS,
        status: 'error' as const
      };
    }
  });
}

// ============================================================================
// UTILIDADES AUXILIARES (público)
// ============================================================================

/**
 * Filtra resultados exitosos de una validación cruzada.
 * 
 * @param results - Array de resultados de fuentes
 * @returns Solo los resultados con status 'success' y resultado no nulo
 */
export function getSuccessfulResults(results: SourceResult[]): SourceResult[] {
  return results.filter(r => r.status === 'success' && r.result !== null);
}

/**
 * Extrae coordenadas de resultados exitosos.
 * 
 * @param results - Array de resultados de fuentes
 * @returns Array de coordenadas UTM de resultados exitosos
 */
export function extractCoordinates(results: SourceResult[]): UTMPoint[] {
  return getSuccessfulResults(results)
    .map(r => r.result!.coordinates);
}

/**
 * Calcula el tiempo total de respuesta (máximo entre todas las fuentes).
 * 
 * @param results - Array de resultados de fuentes
 * @returns Tiempo máximo en milisegundos
 */
export function getTotalResponseTime(results: SourceResult[]): number {
  return Math.max(...results.map(r => r.responseTimeMs), 0);
}
